import React, { useState } from 'react';
import { HeroImage } from '../components/HeroImage';
import { Mail, Send, CheckCircle } from 'lucide-react';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real implementation, this would send to backend
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 3000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactMethods = [
    {
      title: 'General Inquiries',
      email: 'contact@optiflo.app',
      description: 'For general questions and information'
    },
    {
      title: 'Partnerships',
      email: 'contact@optiflo.app',
      description: 'For partnership and collaboration opportunities'
    },
    {
      title: 'Press & Media',
      email: 'contact@optiflo.app',
      description: 'For media inquiries and press requests'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <HeroImage
        title="CONTACT US"
        subtitle="Get in touch with our team. We'll respond as soon as possible."
      />

      <div className="container mx-auto px-5 md:px-10">

        {/* Contact Methods - White Background */}
        <section className="py-20 md:py-32 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {contactMethods.map((method) => (
                <div
                  key={method.title}
                  className="bg-black border border-gray-800 rounded-xl p-6 hover:border-[#f43cde] transition-all hover:shadow-[0_0_30px_rgba(244,60,222,0.3)]"
                >
                  <Mail size={32} className="text-[#f43cde] mb-4" />
                  <h3 className="heading-5 mb-2">{method.title}</h3>
                  <p className="body-small text-on-dark text-muted-dark mb-3">{method.description}</p>
                  <a
                    href={`mailto:${method.email}`}
                    className="link-text text-[#f43cde] hover:underline transition-colors"
                  >
                    {method.email}
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form - Black Background */}
        <section className="py-20 md:py-32 bg-black">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-xl p-8 md:p-12">
              <h2 className="heading-2 mb-8 text-center">Send us a message</h2>
              
              {submitted ? (
                <div className="text-center py-12">
                  <CheckCircle size={64} className="text-[#f43cde] mx-auto mb-4" />
                  <h3 className="heading-4 text-on-white mb-2">Message Sent!</h3>
                  <p className="body-medium text-on-white text-muted-white">
                    Thank you for reaching out. We'll get back to you soon.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="body-small text-on-white font-semibold block mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full bg-white border border-gray-200 text-on-white px-4 py-3 rounded-lg focus:outline-none focus:border-[#f43cde] focus:shadow-[0_0_15px_rgba(244,60,222,0.3)] transition-all"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="body-small text-on-white font-semibold block mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-white border border-gray-200 text-on-white px-4 py-3 rounded-lg focus:outline-none focus:border-[#f43cde] focus:shadow-[0_0_15px_rgba(244,60,222,0.3)] transition-all"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="body-small text-on-white font-semibold block mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full bg-white border border-gray-200 text-on-white px-4 py-3 rounded-lg focus:outline-none focus:border-[#f43cde] focus:shadow-[0_0_15px_rgba(244,60,222,0.3)] transition-all"
                      placeholder="What is this about?"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="body-small text-on-white font-semibold block mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full bg-white border border-gray-200 text-on-white px-4 py-3 rounded-lg focus:outline-none focus:border-[#f43cde] focus:shadow-[0_0_15px_rgba(244,60,222,0.3)] transition-all resize-vertical"
                      placeholder="Tell us more..."
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    <Send size={20} />
                    Send Message
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* Additional Info - White Background */}
        <section className="py-20 md:py-32 bg-white">
          <div className="max-w-3xl mx-auto bg-black border border-gray-800 rounded-xl p-8 text-center">
            <p className="body-medium text-on-dark mb-4">
              <strong className="text-on-dark">Product Support:</strong> For technical support related to specific products, please visit the respective product website.
            </p>
            <p className="body-small text-on-dark text-muted-dark">
              We typically respond to all inquiries within 24-48 business hours.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};